import type { ApiPaginatedResponse } from './Source';

export enum BuzonNucStatusEnum {
  PENDIENTE_FIRMA = 'PENDIENTE_FIRMA',
  HABILITADO = 'HABILITADO',
  DESHABILITADO = 'DESHABILITADO',
  NO_HABILITADO = 'NO_HABILITADO',
  PENDIENTE_DESHABILITAR = 'PENDIENTE_DESHABILITAR',
  PENDIENTE_REHABILITAR = 'PENDIENTE_REHABILITAR',
  RECHAZADO = 'RECHAZADO',
  // CANCELADO = 'CANCELADO',
  // ACEPTADO = 'ACEPTADO',
}

export enum BuzonNucActionEnum {
  /** Usado cuando el NUC está pendiente de firma */
  PENDIENTE_FIRMA = 172,
  /** Usado cuando el NUC está habilitado para recibir notificaciones */
  HABILITADO = 173,
  /** Usado cuando el NUC fue habilitado en un proceso solicitado por el usuario */
  DESHABILITADO = 174,
  ACEPTADO = 175,
  RECHAZADO = 176,
  /** Usado cuando el NUC está Habilitado y el usuario solicita deshabilitarlo */
  PENDIENTE_DESHABILITAR = 177,
  /** Usado cuando el NUC fue deshabilitado en un proceso solicitado por el usuario */
  PENDIENTE_REHABILITAR = 178,
  /** Usado cuando la acción fue cancelada */
  CANCELADO = 179
}

export type ModalidadAudiencia = 'Presencial' | 'Virtual' | 'NoAplica';

export enum BuzonTipoNotificacionEnum {
  CITACION_Y_DOCUMENTO = 'CITACION_Y_DOCUMENTO',
  DOCUMENTO = 'DOCUMENTO',
  CITACION = 'CITACION',
}

export interface BuzonNucPreference {
  idCasoBuzon: number;
  idCaso: number;
  nuc: string;
  /** fecha en que se creó la preferencia de notificación */
  creadoEn: string;
  referencia: string;
  estado: BuzonNucStatusEnum;
  /** Alerta de firma fallida: 'REJECTED' | 'EXPIRED' | null */
  alertaFirma?: 'REJECTED' | 'EXPIRED' | null;
  /** Motivo de la acción realizada */
  motivoAccion?: string;
  /** ID de la solicitud asociada a la acción */
  idSolicitud?: number;
  /** fecha en que se creó el NUC */
  fechaCreado?: string;
  /** Motivo de deshabilitación (cuando estado = DESHABILITADO) */
  motivoDeshabilitacion?: string;
  /** Fecha en que fue deshabilitado */
  fechaDeshabilitacion?: string;
  /**
   * 1 = NUC (habilitación general del caso completo). 2 = Instancia (habilitación filtrada por
   * categoría(s) de tribunal). Mismo valor numérico que se envía en el body de POST /solicitud-activar
   * (confirmado contra GET /api/buzon/nucs real — a pesar de que la documentación ilustrativa del
   * backend mostraba 'NUC'/'Instancia' como string, el valor real es numérico).
   */
  tipoHabilitacion?: 1 | 2;
  /** Categorías de tribunal autorizadas cuando tipoHabilitacion === 2. */
  categoriasAutorizadas?: BuzonCategoriaTribunal[];
}

/** Categoría de tribunal (instancia) — catálogo fijo de 4 valores devueltos por el backend */
export interface BuzonCategoriaTribunal {
  idCategoriaTribunal: number;
  categoriaTribunal: string;
}

/** Categorías disponibles para un caso puntual, devuelto por GET /api/buzon/instancias-disponibles */
export interface BuzonDetalleCasoInstancias {
  idCaso: number;
  categoriasDisponibles: number[];
}

/** Respuesta de GET /api/buzon/instancias-disponibles */
export interface BuzonInstanciasDisponiblesResponse {
  instanciasDisponibles: BuzonCategoriaTribunal[];
  detallePorCaso: BuzonDetalleCasoInstancias[];
}

export interface BuzonDocumentoAdjunto {
  idDocumento: string;
  fileName: string;
  fileUrl: string;
}

export interface BuzonAudienciaDetalle {
  idAudiencia: string;
  /** Asunto del trámite*/
  titulo: string;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  modalidad: ModalidadAudiencia; //'Presencial' | 'Virtual'| 'noAplica';
  ubicacion: string;
  sala: string;
  plazo: string;
}

export interface BuzonNotificacion {
  idCaso: number;
  nuc: string;
  numeroTramite: string;
  asunto: string;
  sala: string;
  tipoNotificacion: BuzonTipoNotificacionEnum;
  plazo: string;
  /** fecha en la que se envió notificación desde el tribunal */
  fechaEnvio: string;
  documentos: BuzonDocumentoAdjunto[];
  audiencia?: BuzonAudienciaDetalle;
  /** ID del trámite asociado a la notificación */
  idTramite: number;
  /** ID de la solicitud de notificación */
  idSolicitudNotificacion: number;
  /** Indica si el usuario ya visualizó la notificación (según la bitácora de visualización) */
  visto: boolean;
}

/** Respuesta del endpoint de IDs de NUCs (sin paginación) */
export interface BuzonNucIdItem {
  idCasoBuzon: number;
  idCaso: number;
  idEstado: number;
}

export type Scope = 'NUC' | 'GENERAL';

export interface BuzonAuthorizationRequest {
  id: string;
  scope: Scope;
  /** Lista de ids de casos asociados a la solicitud */
  nucs: number[];
  fechaSolicitud: string;
  subject: string;
  status: 'PendienteFirma' | 'Firmada';
}

export interface BuzonInactivationRequest {
  /** Identificador único de la solicitud de inactivación */
  idInactivarNotificacion: string; //TODO: revisar con cirico como maneja este id en el backend (INT | STRING )
  scope: Scope;
  /** Lista de ids de casos a inactivar */
  nucs: number[];
  fechaSolicitudInactivar: string;
  documentoJustificacion: string;
  comentario?: string;
}

export interface BuzonJudicialState {
  version: number;
  autoActivarTodoNuc: boolean;
  nucs: BuzonNucPreference[];
  notifications: BuzonNotificacion[];
  authorizationRequests: BuzonAuthorizationRequest[];
  inactivationRequests: BuzonInactivationRequest[];
}

export interface BuzonNotificationIndicators {
  /** total de notificaciones */
  totalNotificaciones: number;
  /** total de notificaciones de citación y documento */
  notificacionCitacionDocumento: number;
  /** total de notificaciones de documentos */
  notificacionDocumento: number;
  /** total de notificaciones de citaciones */
  notificacionCitacion: number;
}

/** Respuesta de GET /api/buzon/notificaciones/no-leidas */
export interface BuzonNoLeidasResponse {
  count: number;
  /** desglose por tipo de las no leídas (mismos campos que `helper.indicadores` del listado) */
  indicadores?: BuzonNotificationIndicators;
}

export interface BuzonNucPageHelper {
  /** total NUC activos */
  totalNUCActivos: number;
  /** indica si se deben activar todos los NUCs automáticamente (incluidas las futuras)*/
  autoActivarTodosNuc: boolean;
}

export interface BuzonNotificationPageHelper {
  indicadores: BuzonNotificationIndicators;
}

export type BuzonNucPageResponse = ApiPaginatedResponse<BuzonNucPreference, BuzonNucPageHelper>;

export type BuzonNotificationPageResponse = ApiPaginatedResponse<BuzonNotificacion, BuzonNotificationPageHelper>;

export interface BuzonNucPageParams {
  idUsuario: number;
  searchTerm?: string; //<-- NUC
  pageNumber?: number;
  pageSize?: number;
  idRelUsuarioEntidades: number | null;
}

export interface BuzonNotificationPageParams {
  idUsuario: number;
  searchTerm?: string; //<-- NUC
  pageNumber?: number;
  pageSize?: number;
  currentPage?: number;
  tipoNotificacion?: BuzonTipoNotificacionEnum | 'TODAS';
  idRelUsuarioEntidades: number | null;
}
/** Resultado de la activación de NUCs */
export interface BuzonActivationResult {
  /** Identificador único de la solicitud de activación */
  idSolicitud: string;
  /** Asunto de la solicitud de activación */
  subject: string;
}
/** Payload para la inactivación de NUCs */
export interface BuzonDeactivatePayload {
  /** Archivo PDF en base64 */
  documentoBase64: string;
  /** Nombre del archivo */
  nombreDocumento: string;
  /** Comentario adicional sobre la inactivación */
  comentario?: string;
  /**
   * Categorías de tribunal específicas a deshabilitar (deshabilitación parcial de un caso Habilitado
   * por instancia). Si se omite, o el caso es Habilitado por NUC, deshabilita el caso completo —
   * comportamiento actual, sin cambios.
   */
  idsCategoriaTribunal?: number[];
}
/** Payload para la rehabilitación de NUCs — mismos campos que activar (NUC completo o instancia(s)
 *  específicas), además del documento de justificación + comentario que la rehabilitación siempre exige. */
export interface BuzonReactivatePayload extends BuzonDeactivatePayload {
  /** 1 = NUC (rehabilita el caso completo, comportamiento actual). 2 = Instancia (rehabilita solo las
   *  categorías indicadas en idsCategoriaTribunal). */
  tipoHabilitacion?: 1 | 2;
}
/** Payload para la activación de NUCs */
export interface BuzonBatchActivationPayload {
  /** Lista de ids de casos a activar */
  idsCasos: number[];
}

/** Opciones de activación por instancia — retrocompatibles (ambas opcionales) */
export interface BuzonActivarOpciones {
  /** 1 = NUC (default, activación general). 2 = Instancia (filtrada por categoría(s) de tribunal). */
  tipoHabilitacion?: 1 | 2;
  /** Requerido solo si tipoHabilitacion === 2. Puede contener una o varias categorías. */
  idsCategoriaTribunal?: number[];
}
/** Payload para la inactivación de NUCs */
export interface BuzonBatchDeactivatePayload {
  /** Lista de ids de casos a inactivar */
  idsCasos: number[];
  /** Archivo PDF en base64 */
  documentoBase64: string;
  /** Nombre del archivo */
  nombreDocumento: string;
  /** Comentario adicional sobre la inactivación */
  comentario?: string;
}

export type BitacoraAccionTipo = 'ABRIR_NOTIFICACION' | 'VISUALIZAR_DOCUMENTO' | 'DESCARGAR_DOCUMENTO'| 'EXPORTAR_CALENDARIO_AUDIENCIA';

export interface BitacoraAccionRequest {
  idUsuario: number;
  idTramite: number;
  idSolicitudNotificacion: number;
  tipoAccion: BitacoraAccionTipo;
  /** JSON string con contexto de la acción (opcional) */
  detalle?: string;
}
